import { assign, isEmpty, mapValues, omitBy } from 'lodash';
import { combineLatest, map } from 'rxjs';
import { distinctUntilChanged } from "rxjs/operators";
import { Component, ChangeDetectionStrategy, signal, ChangeDetectorRef } from "@angular/core";
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { FormGroup, FormControl } from '@angular/forms';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatFacades from '@medsurf/flat-facades';
import { ZodSharedValidator } from '@medsurf/flat-validators';
import { MediaControlService } from '@medsurf/flat-services';

@Component({
    selector: 'medsurf-chapter-main-settings',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './settings.component.html',
    styleUrls: ['./settings.component.scss'],
    standalone: false
})
export class SettingsComponent {
  isSubject = signal(false);
  #isSubmitted = signal(false);
  #settingsFormDefaults = mapValues(FlatModels.NodeEntityModels.nodeSettingDefaults, v => new FormControl(v))
  #settingsFormValidator = mapValues(FlatModels.NodeEntityModels.nodeSettingDefaults, () => true as const);
  inheritedSettings: { [entity: string]: boolean | string | null } | null;
  page: FlatModels.PageEntityModels.PageEntityType | null;
  node: FlatModels.NodeEntityModels.NodeEntityType | null;
	
  pageForm = new FormGroup(
    {
      type: new FormControl(''),
      media: new FormControl(''),
      themeColor: new FormControl(''),
    },
    {
      validators: ZodSharedValidator.validate(
        FlatModels.PageEntityModels.PageSchema.pick({
          type: true
        })
      ),
      updateOn: 'change',
    }
  );
  nodeSettingsForm = new FormGroup(
    this.#settingsFormDefaults,
    {
      validators: ZodSharedValidator.validate(
        FlatModels.NodeEntityModels.NodeSettingsSchema.pick(this.#settingsFormValidator)
      ),
    }
  );
  readonly FlatModels = FlatModels;

  settings$ = combineLatest([
		this.nodeSharedFacade.isBlock$,
	  this.nodeSharedFacade.isQuizStart$
  ]).pipe(map(([isBlock, isQuizStart]) => {
    const activeSettings = FlatModels.NodeEntityModels.SettingsProps.filter((prop) => {
      const onlyUsedBy = new Set(prop.onlyUsedBy || []);
      
      return onlyUsedBy.size === 0 
        || (isBlock && onlyUsedBy.has('block')) 
        || (isQuizStart && onlyUsedBy.has('quiz'));
    });
    return {
      withInheritance: activeSettings.filter(prop => !prop.noInheritance),
      withoutInheritance: activeSettings.filter(prop => prop.noInheritance === true)
    }
  }));
  
  constructor(public nodeSharedFacade: FlatFacades.NodeSharedFacade,
              public historySharedFacade: FlatFacades.HistorySharedFacade,
              public mediaControlService: MediaControlService,
              private cd: ChangeDetectorRef,
              private translate: TranslateService,
              private alertControlFacade: FlatFacades.AlertControlFacade) {
    this.#loadPage();
    this.#loadPageType();
    this.#loadNodeAndSettings();
  }
  
  getControl(form: FormGroup,
             prop: string) {
    return form.controls[prop];
  }

  #loadPage() {
    // Get current Page
    this.nodeSharedFacade.currentSelectedPage$.pipe(takeUntilDestroyed(), distinctUntilChanged()).subscribe((page) => {
      this.page = structuredClone(page);  // probably not nec.
      if (this.page) {
        this.pageForm.reset(this.page);
      }
      this.cd.markForCheck();
    });
  }

  #loadPageType() {
    // Set Page type Validators
    this.nodeSharedFacade.isSubject$.pipe(takeUntilDestroyed()).subscribe((isSubject) => {
	    this.isSubject.set(isSubject);
      if (isSubject) {
        this.pageForm.setValidators(
          ZodSharedValidator.validate(
            FlatModels.PageEntityModels.FolderSchema.pick({
              type: true,
              media: true,
              themeColor: true,
            })
            .merge(
              FlatModels.NodeEntityModels.NodeSchema.pick({
                route: true,
              })
            )
            .merge(
              FlatModels.NodeEntityModels.NodeSettingsSchema.pick(this.#settingsFormValidator)
            )
          )
        );
      } else {
        this.pageForm.setValidators(
          ZodSharedValidator.validate(
            FlatModels.PageEntityModels.PageSchema.pick({
              type: true,
            })
            .merge(
              FlatModels.NodeEntityModels.NodeSchema.pick({
                route: true,
              })
            )
            .merge(
              FlatModels.NodeEntityModels.NodeSettingsSchema.pick(this.#settingsFormValidator)
            )
          )
        );
      }
    });
  }

  #loadNodeAndSettings() {
    this.nodeSharedFacade.currentSelectedNodeSettings$.pipe(takeUntilDestroyed()).subscribe((currentSelectedNodeSettings) => {
      if (currentSelectedNodeSettings) {
        this.node = currentSelectedNodeSettings.node;
        this.inheritedSettings = currentSelectedNodeSettings.settings.inheritedWithDefaults;
        this.nodeSettingsForm.reset(currentSelectedNodeSettings.settings.combinedWithDefaults);
      } else {
        this.node = null;
        this.inheritedSettings = null;
        this.nodeSettingsForm.reset(FlatModels.NodeEntityModels.nodeSettingDefaults);
      }
    });
  }
  
  formatInheritedValue(settingType: typeof FlatModels.NodeEntityModels.SettingsProps[number]['type'], value: boolean | string | null) {
    switch (settingType) {
      case 'color': {
        return FlatModels.ColorConfigModels.colorPickerList.find((color) => color.value === value)?.name ?? `${value}`;
      }
      case 'textarea':
        return `${value || '""'}`.replace(/\n/g, ', ');
      case 'string':
        return `${value || '""'}`;
      case 'boolean':
      default:
        return this.translate.instant(value ? 'global.setting_yes' : 'global.setting_no');
    }
  }

  onSubmit() {
    if (this.#isSubmitted() || this.pageForm.invalid || !this.page) {
      return;
    }
    this.#isSubmitted.set(true);

    // Update Page
    const page: FlatModels.PageEntityModels.PageEntityType = assign(structuredClone(this.page), this.pageForm.value);
    this.nodeSharedFacade.requestUpdatePage(page, 'history.page_update_settings').subscribe({
      error: (result) => {
        ZodSharedValidator.handleError(result, this.alertControlFacade, this.pageForm);
        this.#isSubmitted.set(false);
      },
      next: () => {
        this.#isSubmitted.set(false);
      },
    });
  }

  onAssignShopId(node: FlatModels.NodeEntityModels.NodeEntityType) {
    const newNode = {
      ...node,
      shopId: Math.random().toString(36).substring(2,8).toUpperCase()
    };
    this.nodeSharedFacade.requestUpdateNode(newNode, 'history.node_add_shop_id').subscribe({
      error: (result) => this.alertControlFacade.requestAddError(result)
    });
  }
  
  onMediaChange(mediaId: string | null) {
    this.pageForm.patchValue({media: mediaId});
    return this.onSubmit();
  }

  onResetButton(settingProp: keyof FlatModels.NodeEntityModels.NodeSettings) {
    if (!(settingProp in this.nodeSettingsForm.controls)) return;

    const inheritedSettingsProp = this.inheritedSettings?.[settingProp];
    if (this.nodeSettingsForm.value[settingProp] === inheritedSettingsProp) {
      return;
    }
    this.nodeSettingsForm.controls[settingProp]?.setValue(inheritedSettingsProp);
    this.onSettingChange();
  }

  onSettingChange() {
    if (this.#isSubmitted() || this.nodeSettingsForm.invalid || !this.node) { // removed .dirty for reset button, + probably generally no more needed || !this.nodeSettingsForm.dirty) {
      return;
    }
    this.#isSubmitted.set(true);
    const newSettings = omitBy({...this.nodeSettingsForm.value}, (v, k) => v === this.inheritedSettings?.[k]);
    
    // Update Node Settings
    this.nodeSharedFacade.requestUpdateNodeSettings(this.node.id, isEmpty(newSettings) ? null : newSettings).subscribe({
      error: (result) => {
        ZodSharedValidator.handleError(result, this.alertControlFacade, this.nodeSettingsForm);
        this.#isSubmitted.set(false);
      },
      next: () => {
        this.#isSubmitted.set(false);
      },
    });
  }
}
